import { useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { ActionIcon, Button, Text } from '@repletoryreach/sdk-ui';
import { Textarea } from '@repletoryreach/sdk-ui/inputs';
import { Popover, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useT } from '../../../i18n/I18nProvider';

/** A small, fixed set: enough to be useful, few enough to need no search. */
const EMOJI = ['👍', '🙏', '😊', '👋', '✅', '🎉', '❤️', '😅', '🤝', '📎', '⏰', '💡', '📞', '📍', '🔁', '❓'];

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  sending: boolean;
  disabled: boolean;
  /** The "/" autocomplete, rendered above the field when it is open. */
  picker?: ReactNode;
  /** ARIA wiring for that picker. */
  pickerListId?: string;
  activeOptionId?: string;
  /** Drops a "/" into the field so the picker is reachable without the keyboard. */
  onOpenCanned: () => void;
}

/**
 * The composer.
 *
 * Shaped after `_UI_improvements/tenant/images/inbox.png`: one card holding the
 * field, a toolbar under it and the send button on the right, with the
 * keyboard contract stated in the card rather than in a caption below it.
 *
 * The reference toolbar also carries an attachment clip and a variables
 * button. Neither is here: this composer sends `type: 'text'` only, and a
 * control that looks live and does nothing is worse than one that is absent.
 * Emoji and canned replies both do real work, so those are the two.
 */
export function Composer({
  value, onChange, onSubmit, onKeyDown, textareaRef,
  sending, disabled, picker, pickerListId, activeOptionId, onOpenCanned
}: ComposerProps) {
  const t = useT();
  const [emojiOpen, setEmojiOpen] = useState(false);

  function insertEmoji(emoji: string) {
    const el = textareaRef.current;
    // Insert at the caret rather than appending, so an emoji can go mid-sentence.
    const at = el ? el.selectionStart : value.length;
    onChange(value.slice(0, at) + emoji + value.slice(el ? el.selectionEnd : value.length));
    setEmojiOpen(false);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(at + emoji.length, at + emoji.length);
    });
  }

  return (
    <form
      className="rr-inbox2__composer"
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      <div className="rr-inbox2__composer-field">
        {picker}
        <Textarea
          ref={textareaRef}
          className="rr-inbox2__composer-input"
          placeholder={t('inbox.reply.placeholder')}
          aria-label={t('inbox.send')}
          aria-autocomplete="list"
          aria-controls={pickerListId}
          aria-activedescendant={activeOptionId}
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          minRows={3}
          maxRows={8}
          disabled={disabled}
        />
      </div>

      <div className="rr-inbox2__composer-bar">
        <div className="rr-inbox2__composer-tools">
          <Popover opened={emojiOpen} onChange={setEmojiOpen} position="top-start" width={224}>
            <Popover.Target>
              <ActionIcon variant="subtle" size="md" aria-label={t('inbox.composer.emoji')} disabled={disabled}>
                <SmileIcon />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown>
              <div className="rr-inbox2__emoji">
                {EMOJI.map((e) => (
                  <button key={e} type="button" className="rr-inbox2__emoji-btn" onClick={() => insertEmoji(e)}>
                    {e}
                  </button>
                ))}
              </div>
            </Popover.Dropdown>
          </Popover>

          <Tooltip label={t('inbox.composer.canned')} position="top">
            <ActionIcon
              variant="subtle"
              size="md"
              aria-label={t('inbox.composer.canned')}
              disabled={disabled}
              onClick={onOpenCanned}
            >
              <BoltIcon />
            </ActionIcon>
          </Tooltip>
        </div>

        <Text role="caption" c="var(--rr-color-text-muted)" className="rr-inbox2__composer-hint">
          {t('inbox.composer.hint')}
        </Text>

        <Button type="submit" loading={sending} disabled={disabled || !value.trim()} leftSection={<SendIcon />}>
          {t('inbox.send')}
        </Button>
      </div>
    </form>
  );
}

function SmileIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" aria-hidden>
      <circle cx="10" cy="10" r="7.5" />
      <path d="M7 12c.8.9 1.8 1.4 3 1.4s2.2-.5 3-1.4" />
      <circle cx="7.6" cy="8.2" r="0.9" fill="currentColor" stroke="none" />
      <circle cx="12.4" cy="8.2" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  );
}
function BoltIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M11 2 4 11.5h4.5L9 18l7-9.5h-4.5z" />
    </svg>
  );
}
function SendIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M17.5 2.5 9 11" />
      <path d="M17.5 2.5 12.1 17.5 9 11 2.5 7.9z" />
    </svg>
  );
}
